import { useDeviceIdContext } from '@/components/device-id-provider';
import { Button } from '@/components/ui/button';
import {
  useLivekitAvatar,
  type AvatarCallbacks,
} from '@/hooks/useLivekitAvatar';
import { cn } from '@/lib/utils';
import api from '@/utils/ky';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Mic,
  MicOff,
  PhoneOff,
  Volume2,
  VolumeX,
  Loader2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type AvatarCompanyConfig } from './avatar-configs';

interface LiveKitAvatarCallProps {
  companyConfig: AvatarCompanyConfig;
}

export function LiveKitAvatarCall({ companyConfig }: LiveKitAvatarCallProps) {
  const navigate = useNavigate();
  const { deviceId } = useDeviceIdContext();
  const isZh = companyConfig.language === 'zh';
  const isCxScenario = !!companyConfig.cxScenarioCode;

  const liveKitAvatar = useLivekitAvatar();

  // States
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef(false);

  const handleStartCall = useCallback(async () => {
    setIsInitializing(true);
    setError(null);

    try {
      const participantName = `user-${deviceId || 'anonymous'}`;

      // Create a CX session first so the LiveKit agent can fetch session data
      const scenarioCode = companyConfig.cxScenarioCode;
      let sessionId: string;

      if (scenarioCode) {
        const sessionData = await api
          .post(`api/cx/${scenarioCode}/sessions`, {
            json: {},
          })
          .json<{ sessionId: string; aiConfig: any }>();
        sessionId = sessionData.sessionId;
        console.log('[LiveKitAvatar] CX session created:', sessionId);
      } else {
        sessionId = `${companyConfig.id}-${Date.now()}`;
      }

      const roomName = scenarioCode
        ? `avatar-cx-${scenarioCode}-${sessionId}`
        : `avatar-${sessionId}`;

      const callbacks: AvatarCallbacks = {
        onConnect: () => {
          console.log('[LiveKitAvatar] Connected');
          setIsCallActive(true);
          setIsInitializing(false);
        },
        onError: (err) => {
          console.error('[LiveKitAvatar] Error:', err);
          setError(err?.message || 'Connection error');
          setIsInitializing(false);
        },
        onMessage: (data) => {
          console.log('[LiveKitAvatar] Message:', data.source, data.message);
        },
        onVideoReady: (videoElement) => {
          console.log('[LiveKitAvatar] Video ready');
          if (videoContainerRef.current) {
            // Clear previous video elements
            videoContainerRef.current.innerHTML = '';
            videoElement.style.width = '100%';
            videoElement.style.height = '100%';
            videoElement.style.objectFit = 'cover';
            videoContainerRef.current.appendChild(videoElement);
          }
        },
        onAgentStateChange: (state) => {
          console.log('[LiveKitAvatar] Agent state:', state);
          if (state === 'speaking') {
            setIsProcessing(true);
            setIsListening(false);
          } else if (state === 'listening') {
            setIsProcessing(false);
            setIsListening(true);
          } else {
            setIsProcessing(false);
            setIsListening(false);
          }
        },
      };

      await liveKitAvatar.startAvatarSession(
        {
          firstMessage: companyConfig.firstMessage,
          voiceId: companyConfig.avatar.voiceId,
          roomName,
          participantName,
          avatarImage: companyConfig.hedraAvatarImage,
          avatarId: companyConfig.hedraAvatarId,
          avatarProvider: companyConfig.avatarProvider,
          tavusReplicaId: companyConfig.tavusReplicaId,
          tavusPersonaId: companyConfig.tavusPersonaId,
        },
        callbacks,
      );

      // Attach video to container
      if (videoContainerRef.current) {
        liveKitAvatar.attachToVideoContainer(videoContainerRef);
      }
    } catch (err) {
      console.error('[LiveKitAvatar] Failed to start:', err);
      setError(err instanceof Error ? err.message : 'Failed to start session');
      setIsInitializing(false);
    }
  }, [companyConfig, deviceId, liveKitAvatar]);

  const handleEndCall = useCallback(async () => {
    await liveKitAvatar.stopAvatarSession();
    setIsCallActive(false);
    navigate(`/avatar?company=${companyConfig.id}`);
  }, [liveKitAvatar, navigate, companyConfig.id]);

  const toggleMic = useCallback(() => {
    if (isMicOn) {
      liveKitAvatar.stopMicrophone();
    } else {
      liveKitAvatar.startMicrophone();
    }
    setIsMicOn(!isMicOn);
  }, [isMicOn, liveKitAvatar]);

  // Auto-start on mount
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      handleStartCall();
    }

    return () => {
      liveKitAvatar.stopAvatarSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/avatar?company=${companyConfig.id}`)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {isZh ? '返回' : 'Back'}
            </Button>
            <div className="flex items-center gap-3">
              <Building2
                className="w-5 h-5"
                style={{ color: companyConfig.colors.primary }}
              />
              <h1 className="text-lg font-semibold">
                {companyConfig.name} - {companyConfig.avatar.coachName}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Powered by</span>
              <img
                src="/logos/Hupo_Logotype_Orange(noR).svg"
                alt="Hupo"
                className="h-5"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 max-w-7xl mx-auto p-4 w-full flex flex-col">
        {/* Video area - always rendered so LiveKit can attach video */}
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          <div className="relative bg-slate-900 rounded-xl overflow-hidden flex-1 min-h-0 shadow-2xl">
            {/* Video container - always in DOM */}
            <div ref={videoContainerRef} className="w-full h-full" />

            {/* Loading overlay */}
            <AnimatePresence>
              {isInitializing && (
                <motion.div
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900"
                >
                  <Loader2
                    className="w-12 h-12 animate-spin mb-4"
                    style={{ color: companyConfig.colors.primary }}
                  />
                  <h3 className="text-xl font-semibold mb-2 text-white">
                    {isZh ? `正在连接 ${companyConfig.avatar.coachName}...` : `Connecting to ${companyConfig.avatar.coachName}...`}
                  </h3>
                  <p className="text-slate-400">
                    {isZh ? '正在准备视频会话' : 'Setting up your video session'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error overlay */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                <div className="text-center max-w-md px-6">
                  <h3 className="text-lg font-semibold text-red-400 mb-2">
                    {isZh ? '连接错误' : 'Connection Error'}
                  </h3>
                  <p className="text-red-300 mb-4">{error}</p>
                  <Button variant="outline" className="text-white border-white/30" onClick={() => {
                    hasInitializedRef.current = false;
                    setError(null);
                    handleStartCall();
                  }}>
                    {isZh ? '重试' : 'Try Again'}
                  </Button>
                </div>
              </div>
            )}

            {/* Avatar name overlay */}
            {isCallActive && !isInitializing && (
              <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 z-20">
                <p className="text-white text-sm font-medium">
                  {companyConfig.avatar.coachName}
                </p>
                <p className="text-white/70 text-xs">
                  {isZh ? '培训教练' : 'Training Coach'}
                </p>
              </div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 z-20">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-white text-sm">{isZh ? '正在说话...' : 'Speaking...'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Control bar */}
          {isCallActive && !isInitializing && (
            <div className="bg-white rounded-xl shadow-sm border p-4 flex-shrink-0">
              <div className="flex items-center justify-between px-4">
                {/* Status Indicator */}
                <div className="flex items-center gap-3 w-1/3">
                  <div className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300",
                    isListening
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : isProcessing
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-slate-100 text-slate-500 border border-slate-200"
                  )}>
                    {isListening ? (
                      <>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        {isZh ? '正在聆听' : 'Listening'}
                      </>
                    ) : isProcessing ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {isZh ? '正在说话' : 'Speaking'}
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 rounded-full bg-slate-400" />
                        {isZh ? '连接中...' : 'Connecting...'}
                      </>
                    )}
                  </div>
                </div>

                {/* Center Controls */}
                <div className="flex items-center justify-center gap-4 w-1/3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={toggleMic}
                    className={cn(
                      "rounded-full w-12 h-12 transition-all duration-200",
                      !isMicOn && "bg-red-50 text-red-500 border-red-200 hover:bg-red-100 hover:text-red-600"
                    )}
                  >
                    {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                  </Button>

                  <Button
                    variant="destructive"
                    size="lg"
                    onClick={handleEndCall}
                    className="rounded-full px-8 h-12 gap-2 shadow-md hover:shadow-lg transition-all"
                  >
                    <PhoneOff className="w-5 h-5" />
                    <span className="hidden sm:inline">{isZh ? '结束通话' : 'End Call'}</span>
                  </Button>
                </div>

                {/* Right Spacer */}
                <div className="w-1/3 flex justify-end" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
